// File: noviopus-ats-client/src/features/RecruitmentDashboard/slice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
    RecruitmentDashboardState, 
    FilterStatus, 
    FilterTimeframe, 
    DataMetric 
} from './types';

const initialState: RecruitmentDashboardState = {
    viewMode: 'SINGLE_ORG',
    orgData: [],
    jobData: [],
    chartData: [],
    loading: true,
    error: null,
    filters: {
        status: 'ACTIVE',
        search: '',
        selectedEntityId: null,
        timeframe: 'last_30_days',
        customStartDate: null,
        customEndDate: null,
        dataMetric: 'hiring'
    },
    chartVisible: true 
};

const slice = createSlice({
    name: 'recruitmentDashboard',
    initialState,
    reducers: {
        // --- Data Fetching ---
        fetchData: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchDataSuccess: (state, action: PayloadAction<{ mode: 'multi' | 'single', data: any[], chartData: any[] }>) => {
            state.loading = false;
            state.chartData = action.payload.chartData;
            
            if (action.payload.mode === 'multi') {
                state.viewMode = 'MULTI_ORG';
                state.orgData = action.payload.data;
                state.jobData = []; // Clear other view
            } else {
                state.viewMode = 'SINGLE_ORG';
                state.jobData = action.payload.data;
                state.orgData = []; // Clear other view
            }
        },
        fetchDataFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // --- Filters ---
        setFilterStatus: (state, action: PayloadAction<FilterStatus>) => {
            state.filters.status = action.payload;
        },
        setFilterSearch: (state, action: PayloadAction<string>) => {
            state.filters.search = action.payload;
        },
        setFilterEntityId: (state, action: PayloadAction<string | null>) => {
            state.filters.selectedEntityId = action.payload;
        },
        setFilterTimeframe: (state, action: PayloadAction<FilterTimeframe>) => {
            state.filters.timeframe = action.payload;
        },
        setCustomDateRange: (state, action: PayloadAction<{ start: string | null, end: string | null }>) => {
            state.filters.customStartDate = action.payload.start;
            state.filters.customEndDate = action.payload.end;
        },
        setDataMetric: (state, action: PayloadAction<DataMetric>) => {
            state.filters.dataMetric = action.payload;
        },
        toggleChart: (state) => {
            state.chartVisible = !state.chartVisible;
        },

        // --- Actions (Trigger Sagas) ---
        changeJobStatus: (state, action: PayloadAction<{ id: string, status: number }>) => {
            // Optimistic update could go here, but let's wait for saga refresh
            state.loading = true;
        },
        changeOrgStatus: (state, action: PayloadAction<{ id: string, status: number }>) => {
            state.loading = true;
        },
        deleteJobRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
        },
        
        resetDashboard: () => initialState
    }
});

export const { 
    fetchData, 
    fetchDataSuccess, 
    fetchDataFailure, 
    setFilterStatus, 
    setFilterSearch,
    setFilterEntityId, 
    setFilterTimeframe,
    setCustomDateRange,
    setDataMetric,
    toggleChart, 
    changeJobStatus, 
    changeOrgStatus,
    deleteJobRequest,
    resetDashboard
} = slice.actions;

export default slice.reducer;